import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import type { FC } from 'react';

interface Props {
  id: UniqueIdentifier;
  as?: 'button' | 'div';
  className?: string;
  label: string;
}

/**
 * A grab handle for a sortable item. Render it inside `SortableList`'s
 * `renderItem`, passing the item's `id`, to drag via the handle rather than the
 * whole item (the default `handle` mode).
 *
 * Do **not** nest it inside another interactive element (a clickable card,
 * link, or button): interactive-inside-interactive is invalid and breaks
 * keyboard/assistive-tech behaviour. Lay the handle, the row's click target,
 * and any row actions out as siblings.
 */
const DragHandle: FC<Props> = (props) => {
  const { id, as: Element = 'button', className, label } = props;
  const { attributes, listeners, setActivatorNodeRef, isDragging } =
    useSortable({ id });

  return (
    <Element
      ref={setActivatorNodeRef}
      aria-label={`Reorder: ${label}`}
      className={clstx(
        'bg-transparent p-0 flex items-center justify-center cursor-grab hover:bg-gray-100 rounded',
        isDragging && 'cursor-grabbing',
        className
      )}
      {...(Element === 'button'
        ? { type: 'button' }
        : { role: 'button', onClick: (e) => e.stopPropagation() })}
      {...attributes}
      {...listeners}
    >
      <UntitledIcon name="draggable" className="size-6" />
    </Element>
  );
};

export default DragHandle;
